/**
 * ConsultationResultDialog — Integration Hub
 * -----------------------------------------------------------------------------
 * Mostra o retorno de uma consulta: resumo e JSON completo.
 *
 * O PDF NÃO é exibido aqui: o preview do relatório tem botão próprio na
 * listagem do histórico e usa o componente universal (ImageLightbox).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-29 v1.1.0 - Removidos botões/iframe de PDF (ação separada).
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (resultado da consulta + PDF).
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ConsultationResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título (nome do recurso consultado) */
  title: string;
  /** Payload já sem o conteúdo do PDF */
  payload: unknown;
  /** Informações auxiliares exibidas no topo */
  subtitle?: string;
  /** Mensagem de erro quando a consulta falhou */
  errorMessage?: string | null;
}

export function ConsultationResultDialog({
  open,
  onOpenChange,
  title,
  payload,
  subtitle,
  errorMessage,
}: ConsultationResultDialogProps) {
  // Serializa o payload de forma segura para exibição
  const json = (() => {
    try {
      return JSON.stringify(payload ?? {}, null, 2);
    } catch {
      return '{}';
    }
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            {title}
            {errorMessage ? (
              <Badge variant="destructive">Erro</Badge>
            ) : (
              <Badge variant="secondary">Concluída</Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {subtitle ?? 'Resultado retornado pelo aplicativo integrado.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-4 px-6 py-4">
            {errorMessage && (
              <p className="border-l-2 border-destructive pl-3 text-sm text-destructive">
                {errorMessage}
              </p>
            )}

            {/* O PDF possui botão próprio na listagem do histórico */}



            {/* Payload retornado */}
            <div className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Dados retornados
              </h3>
              <pre className="max-h-[420px] overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed">
                {json}
              </pre>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
